import { useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Select } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import { BaseEdge, Position, getBezierPath, useReactFlow } from '@xyflow/react';
import {
    getFunctionNodeEdgeSlotOptions,
    reconcileEdgeSlotAssignment,
} from '../../constants/edgeInputSlots';
import styles from './index.module.scss';

const COLOR_DEFAULT = '#DEE2E8';
const COLOR_SELECTED = '#006EFF';
const BEZIER_CURVATURE = 0.25;
const ARROW_LENGTH = 10;
const ARROW_HALF_WIDTH = 4;
const DELETE_BUTTON_T = 0.72;

// Keep the visible curve aligned with the card edge instead of the expanded handle hit area.
const HANDLE_EDGE_OFFSET = 22;

const getControlOffset = (distance, curvature) => {
    if (distance >= 0) return 0.5 * distance;
    return 25 * Math.sqrt(-distance) * curvature;
};

const getBezierControlPoint = ({ pos, x1, y1, x2, y2, curvature }) => {
    switch (pos) {
        case Position.Left:
            return [x1 - getControlOffset(x1 - x2, curvature), y1];
        case Position.Right:
            return [x1 + getControlOffset(x2 - x1, curvature), y1];
        case Position.Top:
            return [x1, y1 - getControlOffset(y1 - y2, curvature)];
        case Position.Bottom:
            return [x1, y1 + getControlOffset(y2 - y1, curvature)];
        default:
            return [x1, y1];
    }
};

const getCubicBezierPoint = ({ p0, p1, p2, p3, t }) => {
    const mt = 1 - t;
    const mt2 = mt * mt;
    const t2 = t * t;

    return {
        x: (mt2 * mt * p0.x) + (3 * mt2 * t * p1.x) + (3 * mt * t2 * p2.x) + (t2 * t * p3.x),
        y: (mt2 * mt * p0.y) + (3 * mt2 * t * p1.y) + (3 * mt * t2 * p2.y) + (t2 * t * p3.y),
    };
};

const FlowEdge = ({
    id,
    source,
    target,
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
    selected,
    zIndex,
    data,
}) => {
    const { getNodes, setEdges } = useReactFlow();

    const sx = sourcePosition === Position.Right ? sourceX - HANDLE_EDGE_OFFSET : sourceX + HANDLE_EDGE_OFFSET;
    const tx = targetPosition === Position.Left ? targetX + HANDLE_EDGE_OFFSET : targetX - HANDLE_EDGE_OFFSET;

    const [edgePath, labelX, labelY] = getBezierPath({
        sourceX: sx,
        sourceY,
        sourcePosition,
        targetX: tx,
        targetY,
        targetPosition,
        curvature: BEZIER_CURVATURE,
    });

    const isPending = !!data?.pending;
    const color = isPending ? '#7D8DA1' : (selected ? COLOR_SELECTED : COLOR_DEFAULT);
    const targetNode = getNodes().find((node) => node.id === target);
    const interactionLocked = !!data?.interactionLocked;
    const slotOptions = useMemo(() => {
        if (targetNode?.data?.functionKey === 'templateReplace') {
            return [];
        }

        return getFunctionNodeEdgeSlotOptions(targetNode?.data?.functionKey, targetNode);
    }, [targetNode]);
    const currentSlotKey = slotOptions.some((option) => option.key === data?.inputSlotKey)
        ? data.inputSlotKey
        : slotOptions[0]?.key;
    const [sourceControlX, sourceControlY] = getBezierControlPoint({
        pos: sourcePosition,
        x1: sx,
        y1: sourceY,
        x2: tx,
        y2: targetY,
        curvature: BEZIER_CURVATURE,
    });
    const [targetControlX, targetControlY] = getBezierControlPoint({
        pos: targetPosition,
        x1: tx,
        y1: targetY,
        x2: sx,
        y2: sourceY,
        curvature: BEZIER_CURVATURE,
    });
    const deletePoint = getCubicBezierPoint({
        p0: { x: sx, y: sourceY },
        p1: { x: sourceControlX, y: sourceControlY },
        p2: { x: targetControlX, y: targetControlY },
        p3: { x: tx, y: targetY },
        t: DELETE_BUTTON_T,
    });
    const arrowAngle = Math.atan2(targetY - targetControlY, tx - targetControlX) * (180 / Math.PI);

    const handleDelete = useCallback((event) => {
        event.stopPropagation();
        if (interactionLocked) return;
        if (typeof data?.onDeleteEdge === 'function') {
            data.onDeleteEdge(id);
            return;
        }

        setEdges((prev) => prev.filter((edge) => edge.id !== id));
    }, [data, id, interactionLocked, setEdges]);

    const handleSlotChange = useCallback((nextSlotKey) => {
        if (interactionLocked) return;
        const slotKeys = slotOptions.map((option) => option.key);
        if (slotKeys.length === 0) return;

        if (typeof data?.onSlotChange === 'function') {
            data.onSlotChange(id, nextSlotKey);
            return;
        }

        setEdges((prev) => reconcileEdgeSlotAssignment({
            edges: prev,
            edgeId: id,
            targetId: target,
            slotKeys,
            preferredSlotKey: nextSlotKey,
        }));
    }, [data, id, interactionLocked, setEdges, slotOptions, target]);

    const stopToolbarEvent = useCallback((event) => {
        event.stopPropagation();
    }, []);

    const edgeLabelRoot = typeof document !== 'undefined'
        ? document.querySelector('.react-flow__edgelabel-renderer')
        : null;
    const controlZIndex = Number.isFinite(Number(zIndex)) ? (Number(zIndex) + 1) : 6;

    return (
        <>
            <BaseEdge
                path={edgePath}
                style={{
                    stroke: color,
                    strokeWidth: isPending ? 2.5 : 2,
                    strokeDasharray: isPending ? undefined : '6 4',
                }}
            />
            <path
                d={`M ${tx},${targetY} L ${tx - ARROW_LENGTH},${targetY - ARROW_HALF_WIDTH} L ${tx - ARROW_LENGTH},${targetY + ARROW_HALF_WIDTH} Z`}
                fill={color}
                transform={`rotate(${arrowAngle} ${tx} ${targetY})`}
                pointerEvents="none"
            />

            {!isPending && (slotOptions.length > 0 || selected) && edgeLabelRoot ? createPortal(
                <>
                    {slotOptions.length > 0 ? (
                        <div
                            className={styles.slotSelectShell}
                            style={{
                                position: 'absolute',
                                zIndex: controlZIndex,
                                left: labelX,
                                top: labelY,
                                transform: 'translate(-50%, -50%)',
                            }}
                            onMouseDown={stopToolbarEvent}
                            onClick={stopToolbarEvent}
                        >
                            <Select
                                value={currentSlotKey}
                                options={slotOptions.map((option) => ({
                                    value: option.key,
                                    label: option.label,
                                }))}
                                className={styles.slotSelect}
                                popupClassName={styles.slotDropdown}
                                onChange={handleSlotChange}
                                getPopupContainer={() => edgeLabelRoot}
                                popupMatchSelectWidth={120}
                                popupStyle={{ zIndex: controlZIndex }}
                                bordered={false}
                                disabled={interactionLocked}
                            />
                        </div>
                    ) : null}
                    {selected ? (
                        <div
                            className={styles.toolbarWrapper}
                            style={{
                                zIndex: controlZIndex,
                                left: deletePoint.x,
                                top: deletePoint.y,
                                transform: 'translate(-50%, -50%)',
                            }}
                            onMouseDown={stopToolbarEvent}
                            onClick={stopToolbarEvent}
                        >
                            <button
                                type="button"
                                className={`${styles.deleteBtn} ${interactionLocked ? styles.deleteBtnDisabled : ''}`}
                                disabled={interactionLocked}
                                onClick={handleDelete}
                                aria-label={`Delete edge ${source} to ${target}`}
                            >
                                <CloseOutlined />
                            </button>
                        </div>
                    ) : null}
                </>,
                edgeLabelRoot,
            ) : null}
        </>
    );
};

export default FlowEdge;
